import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Spinner,
  Text,
  VStack,
  Flex,
  Icon,
  Box,
} from "@chakra-ui/react";
import {
  CHAT_TYPE,
  LAW_MODE,
  LAW_TYPES,
  LEGAL_CHATMODE,
  PAGINATION_DEFAULTS,
} from "@root/constants";
import { useDashboard } from "@root/hooks/useDashboard";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ChatIcon from "@root/assets/icons/chaticon";
import { GetChatbotName, getChatRoutebythreadid } from "@root/utils";
import { Paginate } from "../pagination";
import { useNavigate, useLocation } from "react-router-dom";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useProjectsContext } from "@root/context/projects-context";

export const ChatHistoryDrawer = ({
  isOpen,
  onClose,
  chatbotId,
  mode,
  pdf_id,
  projectId,
}: ChatHistoryDrawerProps) => {
  const { t } = useTranslation("common");
  const {
    eachChatbotChatsLoading,
    eachChatbotChats,
    eachchatbotChatsCount,
    fetchEachChatbotChat,
  }: {
    fetchEachChatbotChat: any;
    eachChatbotChats: any;
    eachChatbotChatsLoading: any;
    eachchatbotChatsCount: any;
  } = useDashboard();
  const { resetData } = useMainChatContext();
  const { setDocumentData } = useProjectsContext();

  const [page, setPageNo] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch chats when drawer opens, page changes, or mode changes
  useEffect(() => {
    if (!isOpen) return;

    let payload: any = {
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      page,
    };

    // Handle pdf_id scenario (from UploadDocument)
    if (pdf_id) {
      payload.pdf_id = pdf_id;
    }
    // Handle chatbotId scenario (from other components)
    else if (chatbotId) {
      payload.type = chatbotId;
      if (projectId) {
        payload.projectId = projectId;
      }
      if (chatbotId === CHAT_TYPE.LAW_CHATBOT) {
        payload.source =
          mode == LEGAL_CHATMODE.WEB_SEARCH
            ? LEGAL_CHATMODE.WEB_SEARCH
            : mode == LEGAL_CHATMODE.LAW_INFO_CENTER
            ? LEGAL_CHATMODE.LAW_INFO_CENTER
            : undefined;
      }
    } else {
      // Neither pdf_id nor chatbotId provided, don't fetch
      return;
    }

    fetchEachChatbotChat(payload);
  }, [isOpen, LEGAL_CHATMODE, page, pdf_id, chatbotId, projectId]);

  // Reset page to 1 when drawer opens
  useEffect(() => {
    if (isOpen) {
      setPageNo(1);
    }
  }, [isOpen]);

  // Extract chats by type
  const getChatsByType = (
    chat_type: number,
    selectedOption?: any
  ): { id: number; text: string }[] => {
    if (!eachChatbotChats) return [];

    switch (chat_type) {
      case CHAT_TYPE.LAW_CHATBOT:
        if (
          selectedOption === LAW_MODE.LAW ||
          selectedOption === LAW_MODE.LAW_CASE
        ) {
          return eachChatbotChats.map((chat: any, index: number) => ({
            id: chat?.id ?? index,
            text: chat?.question ?? t("chatHistory.noMessage"),
            ...chat,
          }));
        }
        return [];

      case CHAT_TYPE.PROJECT_CHATBOT:
      case CHAT_TYPE.DRAWING_ANALYSIS_CHAT:
      case CHAT_TYPE.MULTI_DOCUMENTS_QUERY:
      case CHAT_TYPE.KCS:
        return eachChatbotChats.map((chat: any, index: number) => ({
          id: chat?.id ?? index,
          text: chat?.question ?? t("chatHistory.noMessage"),
          ...chat,
        }));

      default:
        return [];
    }
  };

  // Get chats based on the scenario
  const getChats = () => {
    if (pdf_id || chatbotId) {
      // For pdf_id scenario, use the chats directly from the API response
      return (
        eachChatbotChats?.map((chat: any, index: number) => ({
          id: chat?.id,
          text: chat?.question ?? t("chatHistory.noMessage"),
          ...chat,
        })) ?? []
      );
    } else if (chatbotId !== undefined) {
      // For chatbotId scenario, use the existing logic
      return mode ? getChatsByType(chatbotId, mode) : getChatsByType(chatbotId);
    }
    return [];
  };

  const chats = getChats();

  // Render chats list
  const renderChats = () => {
    if (eachChatbotChatsLoading) {
      return (
        <Flex justify="center" align="center" h="100%">
          <Spinner size="lg" />
        </Flex>
      );
    }

    if (!chats?.length) {
      return (
        <Text textAlign="center" color="gray.500">
          {t("chatHistory.noHistoryFound")}
        </Text>
      );
    }

    return (
      <VStack spacing={3} align="stretch">
        {chats?.map((chat: any, index: number) => (
          <Flex
            key={chat?.id ?? index}
            p={2}
            rounded="md"
            flexDirection={"column"}
            justifyContent="flex-start"
            alignItems="flex-start"
            onClick={() => {
              const targetPath = getChatRoutebythreadid(chat);
              const currentPath = location.pathname;

              // Only navigate if target path is different from current path
              if (targetPath !== currentPath) {
                onClose(); // Close drawer before navigation
                resetData();
                if (chat?.type === CHAT_TYPE.DRAWING_ANALYSIS_CHAT) {
                  setDocumentData(null);
                }
                navigate(targetPath);
              } else {
                // If paths are the same, just close the drawer
                onClose();
              }
            }}
            cursor={"pointer"}
            position="relative"
            my={1}
            gap={2}
          >
            <Text fontSize="sm" color="#757575">
              {chat?.chat_title}
            </Text>

            {/* <Icon as={ChatIcon} boxSize={10} mr={2} color="gray.500" /> */}
            <Text fontSize="sm" color="#404040">
              {chat?.name}
            </Text>
            <Box
              position="absolute"
              bottom={0}
              left="8px"
              right="8px"
              height="1px"
              bg="gray.200"
            />
          </Flex>
        ))}
      </VStack>
    );
  };

  const renderName = () => {
    if (chatbotId) {
      return GetChatbotName(chatbotId, t, mode);
    }
    if (pdf_id) {
      return `${t("upload_document.pdf")}# ${pdf_id}`;
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
      <DrawerContent bg="white" width="250px">
        <DrawerCloseButton />
        <DrawerHeader>
          {` ${renderName()} ${t("chatHistory.title")} `}
        </DrawerHeader>

        <DrawerBody
          css={{
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#d1d1d1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#b8b8b8",
            },
          }}
        >
          {renderChats()}
        </DrawerBody>

        <Flex justifyContent={"center"} mt={4} flexWrap="wrap">
          <Paginate
            page={page}
            count={eachchatbotChatsCount}
            pageSize={PAGINATION_DEFAULTS.PAGESIZE}
            onPageChange={setPageNo}
          />
        </Flex>
      </DrawerContent>
    </Drawer>
  );
};
